import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  History,
  Loader2,
  RefreshCw,
  Sparkles,
  WalletCards,
  Zap,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type CreditAccountSummary = {
  balanceCredits: number;
  trialCreditsRemaining: number;
  availableCredits: number;
  lowCreditThreshold: number;
  autoTopupEnabled: boolean;
  autoTopupAmountCents: number;
  canAutoTopup: boolean;
  low: boolean;
  exceeded: boolean;
};

type UsageEvent = {
  id: string;
  provider: string;
  model: string;
  tokens_used: number;
  credits_used: number;
  created_at: string;
};

type LedgerEntry = {
  id: string;
  entry_type: string;
  credits_delta: number;
  amount_cents: number | null;
  provider: string | null;
  model: string | null;
  token_count: number | null;
  created_at: string;
};

type CreditPayload = {
  account: CreditAccountSummary;
  usageEvents: UsageEvent[];
  ledger: LedgerEntry[];
};

const quickTopups = [
  { amount: 10, credits: 1000 },
  { amount: 25, credits: 2500 },
  { amount: 50, credits: 5000 },
  { amount: 100, credits: 10000 },
];

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatCurrencyFromCents(cents: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format((cents || 0) / 100);
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function friendlyEntryType(type: string) {
  return type
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function AICreditManager({ embedded = false }: { embedded?: boolean }) {
  const { session } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [payload, setPayload] = useState<CreditPayload | null>(null);
  const [customAmount, setCustomAmount] = useState('25');
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  const [autoTopupEnabled, setAutoTopupEnabled] = useState(false);
  const [lowCreditThreshold, setLowCreditThreshold] = useState(100);
  const [autoTopupAmount, setAutoTopupAmount] = useState('10');

  const account = payload?.account;

  const loadCredits = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!session?.access_token) return;

    if (quiet) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const { data, error } = await supabase.functions.invoke('get-ai-credit-account', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Could not load AI credits.');

      const nextPayload = data as CreditPayload;
      setPayload(nextPayload);
      setAutoTopupEnabled(nextPayload.account.autoTopupEnabled);
      setLowCreditThreshold(nextPayload.account.lowCreditThreshold);
      setAutoTopupAmount(String(Math.max(5, nextPayload.account.autoTopupAmountCents / 100)));
    } catch (error: any) {
      toast({
        title: 'Could not load AI credits',
        description: String(error?.message || error),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session?.access_token, toast]);

  useEffect(() => {
    void loadCredits();
  }, [loadCredits]);

  useEffect(() => {
    const refresh = () => void loadCredits({ quiet: true });
    window.addEventListener('mapleos:credits-refresh', refresh);
    return () => window.removeEventListener('mapleos:credits-refresh', refresh);
  }, [loadCredits]);

  const status = useMemo(() => {
    if (!account) return null;
    if (account.exceeded) {
      return {
        icon: AlertTriangle,
        className: 'border-red-500/30 bg-red-500/10 text-red-200',
        text: 'Your AI credits are depleted. Add credits to keep using paid AI models.',
      };
    }
    if (account.low) {
      return {
        icon: AlertTriangle,
        className: 'border-amber-400/30 bg-amber-400/10 text-amber-100',
        text: 'Your AI credits are running low. Consider topping up soon.',
      };
    }
    return {
      icon: CheckCircle2,
      className: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-100',
      text: 'Your AI credits are in a healthy range.',
    };
  }, [account]);

  const startTopup = async (amountUsd: number) => {
    if (!session?.access_token) return;
    const amountCents = Math.round(amountUsd * 100);

    if (amountCents < 500) {
      toast({ title: 'Minimum top-up is $5.00', variant: 'destructive' });
      return;
    }

    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-ai-credit-checkout', {
        body: {
          amountCents,
          savePaymentMethod,
          returnUrl: '/credits',
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Could not start checkout.');
      if (!data?.url) throw new Error('No checkout URL returned.');

      window.location.href = data.url;
    } catch (error: any) {
      toast({
        title: 'Could not start checkout',
        description: String(error?.message || error),
        variant: 'destructive',
      });
      setCheckoutLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!session?.access_token || !account) return;

    setSavingSettings(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-ai-credit-settings', {
        body: {
          lowCreditThreshold,
          autoTopupEnabled,
          autoTopupAmountCents: Math.round(Number(autoTopupAmount || 0) * 100),
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message || 'Could not save billing settings.');

      setPayload((current) => current ? { ...current, account: data.account } : current);
      toast({ title: 'Billing settings saved' });
    } catch (error: any) {
      toast({
        title: 'Could not save billing settings',
        description: String(error?.message || error),
        variant: 'destructive',
      });
      setAutoTopupEnabled(account.autoTopupEnabled);
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-border/70 bg-card/80 shadow-none">
        <CardContent className="flex min-h-[360px] items-center justify-center">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            Loading AI credits…
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!account || !payload) {
    return (
      <Card className="border-border/70 bg-card/80 shadow-none">
        <CardContent className="space-y-4 py-10 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-400" />
          <div>
            <h2 className="text-lg font-semibold">AI credits are unavailable</h2>
            <p className="mt-1 text-sm text-muted-foreground">Refresh the page or try again in a moment.</p>
          </div>
          <Button onClick={() => void loadCredits({ quiet: true })}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  const StatusIcon = status?.icon || CheckCircle2;

  return (
    <div className={cn('space-y-6', embedded && 'rounded-3xl')}>
      <Card className="overflow-hidden border-blue-500/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white shadow-none">
        <CardHeader className="border-b border-white/10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Sparkles className="h-5 w-5 text-primary" />
                CanXP AI usage billing
              </CardTitle>
              <CardDescription className="mt-2 text-slate-300">
                CanXP AI provides trial AI credits to all users. After that, your credit balance can be topped up as needed.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              onClick={() => void loadCredits({ quiet: true })}
              disabled={refreshing}
            >
              {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-sm text-slate-400">Credits remaining</p>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                <span className="text-5xl font-bold leading-none tracking-tight">
                  {formatNumber(account.availableCredits)}
                </span>
                <span className="pb-1 text-sm text-slate-400">credits</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge variant="outline" className="border-white/15 bg-white/5 text-slate-200">
                  Trial remaining: {formatNumber(account.trialCreditsRemaining)}
                </Badge>
                <Badge variant="outline" className="border-white/15 bg-white/5 text-slate-200">
                  Purchased balance: {formatNumber(account.balanceCredits)}
                </Badge>
              </div>
            </div>
            {status && (
              <div className={cn('flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm', status.className)}>
                <StatusIcon className="h-4 w-4 shrink-0" />
                <span>{status.text}</span>
              </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm text-slate-400">Low credit threshold</p>
              <p className="mt-2 text-2xl font-bold">{formatNumber(account.lowCreditThreshold)}</p>
              <p className="text-sm text-slate-400">credits</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm text-slate-400">Auto top-up amount</p>
              <p className="mt-2 text-2xl font-bold">{formatCurrencyFromCents(account.autoTopupAmountCents)}</p>
              <p className="text-sm text-slate-400">{formatNumber(account.autoTopupAmountCents)} credits</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <p className="text-sm text-slate-400">Trial used</p>
              <p className="mt-2 text-2xl font-bold">{formatNumber(Math.max(0, 250 - account.trialCreditsRemaining))}</p>
              <p className="text-sm text-slate-400">of 250 starter credits</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/70 bg-card/90 shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <CreditCard className="h-5 w-5 text-primary" />
            Billing and top-ups
          </CardTitle>
          <CardDescription>
            Add credits instantly, save a payment method, and enable auto top-up if you want CanXP AI to reload credits automatically when you run low.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <Label>Quick top-up</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {quickTopups.map((topup) => (
                <button
                  key={topup.amount}
                  type="button"
                  className="flex items-center justify-between rounded-lg border bg-background px-4 py-3 text-sm transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={() => void startTopup(topup.amount)}
                  disabled={checkoutLoading}
                >
                  <span className="font-semibold">${topup.amount.toFixed(2)}</span>
                  <span className="text-muted-foreground">{formatNumber(topup.credits)} credits</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-topup">Custom amount in USD</Label>
            <div className="flex gap-2">
              <Input
                id="custom-topup"
                type="number"
                min="5"
                step="1"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
              <Button onClick={() => void startTopup(Number(customAmount || 0))} disabled={checkoutLoading}>
                {checkoutLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Top up
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatNumber(Math.max(0, Math.round(Number(customAmount || 0) * 100)))} credits will be added.
            </p>
          </div>

          <div className="rounded-2xl border bg-background/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base">Save payment method</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your next top-up can save the payment method for future auto reloads.
                </p>
              </div>
              <Switch checked={savePaymentMethod} onCheckedChange={setSavePaymentMethod} />
            </div>
          </div>

          <div className="border-t pt-6" />

          <div className="rounded-2xl border bg-background/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-base">Enable auto top-up</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  CanXP AI will automatically buy more credits when you run low.
                </p>
              </div>
              <Switch
                checked={autoTopupEnabled}
                onCheckedChange={setAutoTopupEnabled}
                disabled={!account.canAutoTopup}
              />
            </div>
            {!account.canAutoTopup && (
              <p className="mt-3 rounded-lg border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-200">
                Save a payment method through your next top-up before enabling auto top-up.
              </p>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="low-credit-threshold">Alert me when credits reach</Label>
              <span className="text-sm text-muted-foreground">{formatNumber(lowCreditThreshold)} credits</span>
            </div>
            <Input
              id="low-credit-threshold"
              type="range"
              min={25}
              max={1000}
              step={25}
              value={lowCreditThreshold}
              onChange={(event) => setLowCreditThreshold(Number(event.target.value))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="auto-topup-amount">Auto top-up amount</Label>
            <Input
              id="auto-topup-amount"
              type="number"
              min="5"
              step="1"
              value={autoTopupAmount}
              onChange={(event) => setAutoTopupAmount(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {formatNumber(Math.max(0, Math.round(Number(autoTopupAmount || 0) * 100)))} credits will be added each time CanXP AI reloads your balance.
            </p>
          </div>

          <Button className="w-full" onClick={() => void saveSettings()} disabled={savingSettings}>
            {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save billing settings
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/70 bg-card/90 shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Zap className="h-5 w-5 text-primary" />
            Recent AI usage
          </CardTitle>
          <CardDescription>
            Paid API usage is tracked by tokens and converted into credit consumption.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payload.usageEvents.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No AI usage has been billed yet.
            </div>
          ) : (
            <div className="divide-y rounded-2xl border">
              {payload.usageEvents.slice(0, 10).map((event) => (
                <div key={event.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{event.model}</p>
                    <p className="text-sm text-muted-foreground">
                      {event.provider} · {formatDate(event.created_at)}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatNumber(event.tokens_used)} tokens</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatNumber(event.credits_used)} credits</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/70 bg-card/90 shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <History className="h-5 w-5 text-primary" />
            Credit ledger
          </CardTitle>
          <CardDescription>
            A running history of top-ups, trial grants, bonuses, and usage deductions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {payload.ledger.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">
              No credit ledger entries yet.
            </div>
          ) : (
            <div className="divide-y rounded-2xl border">
              {payload.ledger.slice(0, 12).map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <p className="font-semibold">{friendlyEntryType(entry.entry_type)}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(entry.created_at)}</p>
                    {entry.model && (
                      <p className="truncate text-xs text-muted-foreground">{entry.provider} · {entry.model}</p>
                    )}
                  </div>
                  <div className={cn('text-right font-semibold', entry.credits_delta >= 0 ? 'text-emerald-500' : 'text-red-500')}>
                    {entry.credits_delta >= 0 ? '+' : ''}{formatNumber(entry.credits_delta)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border bg-card/70 p-5">
          <WalletCards className="mb-3 h-5 w-5 text-primary" />
          <h3 className="font-semibold">Shared balance</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            These credits use the same CanXP AI profile and balance as MapleOS.
          </p>
        </div>
        <div className="rounded-2xl border bg-card/70 p-5">
          <CheckCircle2 className="mb-3 h-5 w-5 text-primary" />
          <h3 className="font-semibold">Simple conversion</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            $1.00 adds 100 AI credits. Paid API calls consume credits based on token usage.
          </p>
        </div>
      </div>
    </div>
  );
}
